import { TranslationKey } from '../translation/TranslationKey';
import { cloneDeep } from '@apollo/client/utilities';
import { CompanyEntity } from '../common/entity';
import { Unit, UnitOfMeasure, UnitSystem, UnitType } from '../uom';
import { translate } from '../translation/Translator';
import { CustomFieldValue, WithCustomFields } from '../custom_field/customField';
import { applyMixins } from '../../util/mixins';
import { UnitOfMeasureUtil } from '../uom';

enum ProductMasterDataValidationError {
  name = 'Please enter a product name',
  number = 'Please enter a product number',
  kitManagedUnitType = 'Products that are kit managed must have a unit type of quantity',
}

export class ProductMasterData extends CompanyEntity {
  productName!: string;
  productNumber!: string;

  lotManaged?: boolean = false;
  serialManaged?: boolean = false;
  kitManaged?: boolean = false;
  handlingUnit?: boolean = false;

  countryOfOrigin?: string;
  reorderPoint?: string;

  grossWeight?: Weight;
  netWeight?: Weight;

  dimensions?: Dimensions;

  purchasePrice?: Price;
  sellingPrice?: Price;

  productCategory?: string;

  image?: string | null;

  unitOfMeasure?: UnitOfMeasure;

  // EAN
  globalTradeItemNumber?: string;

  customFields: Map<string, CustomFieldValue> = new Map();

  expiryTracking: boolean = true;

  labels: string[] = [];

  constructor(obj: any) {
    if (!obj.companyId) return;
    super(obj.companyId);
    Object.assign(this, obj);
    this.customFields = WithCustomFields.toCustomFieldsMap(obj);
    this.parseFromJSON();
  }

  parseFromJSON() {
    if (this.dimensions) this.dimensions = new Dimensions(this.dimensions);
    if (this.grossWeight) this.grossWeight = new Weight(this.grossWeight);
    if (this.netWeight) this.netWeight = new Weight(this.netWeight);
    if (this.purchasePrice) this.purchasePrice = new Price(this.purchasePrice);
    if (this.sellingPrice) this.sellingPrice = new Price(this.sellingPrice);
  }

  withProductName(productName: string) {
    this.productName = productName;
    return cloneDeep(this);
  }

  withProductNumber(productNumber: string) {
    this.productNumber = productNumber;
    return cloneDeep(this);
  }

  withLotManaged(lotManaged: boolean) {
    this.lotManaged = lotManaged;
    return cloneDeep(this);
  }

  withSerialManaged(serialManaged: boolean) {
    this.serialManaged = serialManaged;
    return cloneDeep(this);
  }

  withKitManaged(kitManaged: boolean) {
    this.kitManaged = kitManaged;
    if (kitManaged) this.withUnitType(UnitType.quantity);
    return cloneDeep(this);
  }

  withHandlingUnit(handlingUnit: boolean) {
    this.handlingUnit = handlingUnit;
    return cloneDeep(this);
  }

  withCountryOfOrigin(countryOfOrigin?: string) {
    this.countryOfOrigin = countryOfOrigin;
    return cloneDeep(this);
  }

  withReorderPoint(reorderPoint: string) {
    this.reorderPoint = reorderPoint;
    return cloneDeep(this);
  }

  withEAN(ean: string) {
    this.globalTradeItemNumber = ean;
    return cloneDeep(this);
  }

  withUnitType(unitType?: UnitType) {
    if (!this.unitOfMeasure) this.unitOfMeasure = {};

    this.unitOfMeasure.unitType = unitType;
    this.unitOfMeasure.unit = undefined;

    return this.withUnitSystem(UnitSystem.metric);
  }

  withUnitSystem(system?: UnitSystem) {
    if (!this.unitOfMeasure) this.unitOfMeasure = {};
    this.unitOfMeasure.system = system;
    return this.withUnit(UnitOfMeasureUtil.defaultUnit(this.unitOfMeasure));
  }

  withUnit(unit?: Unit) {
    if (!this.unitOfMeasure) this.unitOfMeasure = {};
    this.unitOfMeasure.unit = unit;
    return cloneDeep(this);
  }

  withHeight(height: string) {
    if (!this.dimensions) this.dimensions = new Dimensions();
    this.dimensions.height = height;
    return cloneDeep(this);
  }

  withWidth(width: string) {
    if (!this.dimensions) this.dimensions = new Dimensions();
    this.dimensions.width = width;
    return cloneDeep(this);
  }

  withDepth(depth: string) {
    if (!this.dimensions) this.dimensions = new Dimensions();
    this.dimensions.depth = depth;
    return cloneDeep(this);
  }

  withLengthUnit(unit?: LengthUnit) {
    if (!this.dimensions) {
      this.dimensions = new Dimensions(unit);
    } else {
      (this.dimensions.unit as any) = unit;
    }

    return cloneDeep(this);
  }

  withNetWeightValue(value: string) {
    if (!this.netWeight) this.netWeight = new Weight();
    this.netWeight.value = value;
    return cloneDeep(this);
  }

  withNetWeightUnit(unit?: WeightUnit) {
    if (!this.netWeight) this.netWeight = new Weight({ unit: unit });
    (this.netWeight.unit as any) = unit;
    return cloneDeep(this);
  }

  withGrossWeightValue(value: string) {
    if (!this.grossWeight) this.grossWeight = new Weight();
    this.grossWeight.value = value;
    return cloneDeep(this);
  }

  withGrossWeightUnit(unit?: WeightUnit) {
    if (!this.grossWeight) this.grossWeight = new Weight({ unit: unit });
    (this.grossWeight.unit as any) = unit;
    return cloneDeep(this);
  }

  withPurchasePriceValue(value: string) {
    if (!this.purchasePrice) this.purchasePrice = new Price();
    this.purchasePrice.value = value;
    return cloneDeep(this);
  }

  withPurchasePriceCurrency(currency?: Currency) {
    if (!this.purchasePrice) this.purchasePrice = new Price(currency);
    (this.purchasePrice.currency as any) = currency;
    return cloneDeep(this);
  }

  withSellingPriceValue(value: string) {
    if (!this.sellingPrice) this.sellingPrice = new Price();
    this.sellingPrice.value = value;
    return cloneDeep(this);
  }

  withSellingPriceCurrency(currency?: Currency) {
    if (!this.sellingPrice) this.sellingPrice = new Price(currency);
    (this.sellingPrice.currency as any) = currency;
    return cloneDeep(this);
  }

  withImage(image?: string | null) {
    this.image = image;
    return cloneDeep(this);
  }

  withExpiryTracking(expiryTracking: boolean) {
    this.expiryTracking = expiryTracking;
    return cloneDeep(this);
  }

  withLabel(label: string) {
    if (!this.labels) this.labels = [];
    if (!this.labels.includes(label)) this.labels.push(label);
    return cloneDeep(this);
  }

  withLabels(labels: string[]) {
    this.labels = labels;
    return cloneDeep(this);
  }

  static isQuantityManaged(pmd: ProductMasterData) {
    return !pmd.serialManaged && !pmd.lotManaged && !pmd.kitManaged;
  }

  static manageTypesFrom(pmd: ProductMasterData): Set<ProductMasterDataManageType> {
    const types = new Set<ProductMasterDataManageType>();
    if (pmd.serialManaged) types.add(ProductMasterDataManageType.serial);
    if (pmd.lotManaged) types.add(ProductMasterDataManageType.lot);
    if (pmd.kitManaged) types.add(ProductMasterDataManageType.kit);
    if (ProductMasterData.isQuantityManaged(pmd)) types.add(ProductMasterDataManageType.quantity);
    return types;
  }
}

export interface ProductMasterData extends WithCustomFields {}
applyMixins(ProductMasterData, [ProductMasterData, WithCustomFields]);

export class Weight {
  value?: string;
  unit: WeightUnit;

  constructor(obj?: any) {
    this.value = String(obj?.value);
    this.unit = obj?.unit || WeightUnit.gram;
  }

  parse() {
    return {
      value: parseFloat(this.value || '0') || 0,
      unit: this.unit,
    };
  }
}

export class Dimensions {
  height?: string;
  width?: string;
  depth?: string;
  unit: LengthUnit;

  constructor(obj?: any) {
    this.unit = obj?.unit || LengthUnit.meter;
    this.height = String(obj?.height);
    this.width = String(obj?.width);
    this.depth = String(obj?.depth);
  }

  parse() {
    return {
      height: parseFloat(this.height || '0') || 0,
      width: parseFloat(this.width || '0') || 0,
      depth: parseFloat(this.depth || '0') || 0,
      unit: this.unit,
    };
  }
}

export class Price {
  value?: string;
  currency: Currency;

  constructor(obj?: any) {
    this.value = String(obj?.value);
    this.currency = obj?.currency || Currency.EUR;
  }

  parse() {
    return {
      value: parseFloat(this.value || '0') || 0,
      currency: this.currency,
    };
  }
}

export type Url = string;
export type RGBColor = string;
export type uuid = string;

export type int = number;

export enum MetricWeightUnit {
  gram = 'gram',
  kilogram = 'kilogram',
}

export enum ImperialWeightUnit {
  pound = 'pound',
  ounce = 'ounce',
}

export enum WeightUnit {
  gram = 'gram',
  kilogram = 'kilogram',
  pound = 'pound',
  ounce = 'ounce',
}

export enum MetricLengthUnit {
  centimeter = 'centimeter',
  decimeter = 'decimeter',
  meter = 'meter',
}

export enum LengthUnit {
  millimeter = 'millimeter',
  centimeter = 'centimeter',
  decimeter = 'decimeter',
  meter = 'meter',
  inch = 'inch',
  foot = 'foot',
  yard = 'yard',
}

export enum Currency {
  USD = 'USD',
  EUR = 'EUR',
  JPY = 'JPY',
  GBP = 'GBP',
  AUD = 'AUD',
  CAD = 'CAD',
  CHF = 'CHF',
  CNY = 'CNY',
  HKD = 'HDK',
  NZD = 'NZD',
}

export function weightUnitToString(unit: WeightUnit) {
  switch (unit) {
    case WeightUnit.gram:
      return translate({ key: TranslationKey.g });
    case WeightUnit.kilogram:
      return translate({ key: TranslationKey.kg });
    case WeightUnit.ounce:
      return translate({ key: TranslationKey.oz });
    case WeightUnit.pound:
      return translate({ key: TranslationKey.lb });
  }
}

export function lengthUnitToString(unit: LengthUnit) {
  switch (unit) {
    case LengthUnit.centimeter:
      return translate({ key: TranslationKey.cm });
    case LengthUnit.decimeter:
      return translate({ key: TranslationKey.dm });
    case LengthUnit.millimeter:
      return translate({ key: TranslationKey.mm });
    case LengthUnit.meter:
      return translate({ key: TranslationKey.m });
    case LengthUnit.foot:
      return 'foot';
    case LengthUnit.inch:
      return 'inch';
    case LengthUnit.yard:
      return 'yard';
  }
}

export const ProductMasterDataManageType = {
  serial: 'serial',
  lpn: 'lpn',
  lot: 'lot',
  kit: 'kit',
  quantity: 'quantity',
} as const;

export type ProductMasterDataManageType =
  (typeof ProductMasterDataManageType)[keyof typeof ProductMasterDataManageType];

export const isProductMasterDataManageType = (value: string): value is ProductMasterDataManageType =>
  Object.values(ProductMasterDataManageType).includes(value as ProductMasterDataManageType);
